/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-10-13
 */

import { useState } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { Language as LanguageIcon, Check } from '@mui/icons-material';
import {
  changeLanguage,
  getCurrentLanguage,
  SUPPORTED_LANGUAGES,
  type SupportedLanguageCode,
} from '../../utils/language';

/**
 * Language selector dropdown component
 * Allows users to switch between supported languages (English, Spanish, French)
 */
const LanguageSelector: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const currentLanguage = getCurrentLanguage();

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = async (languageCode: SupportedLanguageCode) => {
    await changeLanguage(languageCode);
    handleClose();
  };

  return (
    <Box>
      <Tooltip title="Change Language">
        <IconButton
          onClick={handleOpen}
          aria-label="Change language"
          aria-controls="language-menu"
          aria-haspopup="true"
          aria-expanded={Boolean(anchorEl)}
          color="inherit"
        >
          <LanguageIcon />
        </IconButton>
      </Tooltip>
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        MenuListProps={{
          'aria-label': 'Select language',
          role: 'menu',
        }}
      >
        {Object.entries(SUPPORTED_LANGUAGES).map(([code, language]) => {
          const isSelected = currentLanguage === code;
          return (
            <MenuItem
              key={code}
              onClick={() =>
                handleLanguageChange(code as SupportedLanguageCode)
              }
              selected={isSelected}
              role="menuitemradio"
              aria-checked={isSelected}
            >
              {isSelected && (
                <ListItemIcon>
                  <Check />
                </ListItemIcon>
              )}
              <ListItemText
                inset={!isSelected}
                primary={language.nativeName}
                secondary={language.name}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

export default LanguageSelector;
